import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type {
  ChannelProviderName,
  ChannelConnectResponse,
  ChannelsOnboardingMessage,
  OnboardingState,
} from '../pages/admin/channels-types.js'

const POPUP_FEATURES = 'width=600,height=700,scrollbars=yes,resizable=yes'
// Each launch() gets its own window name instead of a shared constant.
// Reusing one name across launches (e.g. Reconnect right after a previous
// attempt) makes window.open() attach to whatever window already has that
// name instead of opening a fresh one, so popupRef ends up tracking a stale
// handle whose "closed" state can flip true before the new OAuth flow has
// even finished — firing the completion fallback (and the accounts refetch)
// too early, before the backend callback has run.
let popupSequence = 0
const TIMEOUT_MS = 5 * 60 * 1000
const POPUP_POLL_MS = 500
// Meta's callback chains several sequential Graph API calls (code exchange,
// long-lived exchange, businesses, WABAs, phone numbers, pages, IG lookup)
// and has been observed taking 4-6+ seconds end-to-end in production — far
// longer than the popup takes to close afterward. A single pending-selection
// check right when the popup closes routinely loses that race and finds
// nothing yet, silently treating the whole connection as "complete" with no
// asset ever persisted. Retry for a while instead of checking once.
const PENDING_SELECTION_RETRY_MS = 1000
const PENDING_SELECTION_MAX_ATTEMPTS = 15

export interface UseChannelsOnboardingArgs {
  provider: ChannelProviderName
  onComplete: () => Promise<void> | void
  onPendingAssetSelection?: ((assets: Array<{ id: string; name: string }>) => void) | undefined
}

export interface UseChannelsOnboardingReturn {
  state: OnboardingState
  error: string | null
  launch: () => Promise<void>
  cancel: () => void
}

export function useChannelsOnboarding({
  provider,
  onComplete,
  onPendingAssetSelection,
}: UseChannelsOnboardingArgs): UseChannelsOnboardingReturn {
  const [state, setState] = useState<OnboardingState>('idle')
  const [error, setError] = useState<string | null>(null)

  const popupRef = useRef<Window | null>(null)
  const messageHandlerRef = useRef<((e: MessageEvent) => void) | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cleanup = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current)
      messageHandlerRef.current = null
    }
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  const cancel = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close()
    }
    popupRef.current = null
    cleanup()
    setState('idle')
    setError(null)
  }, [cleanup])

  const launch = useCallback(async () => {
    // Tear down any interval/listener left over from a previous incomplete
    // launch() call before starting a new one — otherwise an orphaned poll
    // interval keeps running against the shared popupRef/messageHandlerRef,
    // and can fire a stale outcome (using that earlier call's closures)
    // once the newest popup closes.
    cleanup()
    setError(null)
    setState('opening')

    let resp: ChannelConnectResponse
    try {
      resp = await api.post<ChannelConnectResponse>('/admin/channels/connect', { provider })
    } catch (e) {
      setError(`Failed to start ${provider} onboarding: ${(e as Error).message}`)
      setState('failed')
      return
    }

    const popupName = `channels-onboarding-${++popupSequence}`
    const popup = window.open(resp.authUrl, popupName, POPUP_FEATURES)
    if (popup === null) {
      setError('Popup blocked. Please allow popups for this site and try again.')
      setState('failed')
      return
    }
    popupRef.current = popup

    // Tracks whether a definitive outcome (complete/error/pending-selection)
    // has already been delivered via postMessage, so the popup-closed poll
    // fallback below never overrides it. Without this guard, a race between
    // the popup's postMessage and its own window.close() could let the poll
    // fire onComplete() a few hundred ms later and silently skip the
    // pending-asset-selection flow entirely (observed with YouTube: the
    // popup closes, but the asset picker never opens and nothing is stored).
    let outcomeDelivered = false

    const handleMessage = (ev: MessageEvent): void => {
      // eslint-disable-next-line no-console
      console.debug('[onboarding] message event received', {
        origin: ev.origin,
        expectedOrigin: window.location.origin,
        // JSON.stringify forces full serialization instead of a lazy/collapsed
        // console object preview, so the actual shape (or 'undefined'/'{}') is
        // visible even after the event has been GC'd or mutated.
        dataJson: (() => { try { return JSON.stringify(ev.data) } catch { return String(ev.data) } })(),
        dataType: typeof ev.data,
      })
      // The OAuth popup navigates through Facebook's domain and then to the
      // API server's callback URL (e.g. an ngrok tunnel in dev), so ev.origin
      // will be the API server's origin — not window.location.origin (the SPA).
      // Rejecting based on origin breaks the postMessage flow entirely in dev,
      // forcing reliance on the slower fallback poll. Instead, verify the
      // message came from our popup via ev.source and has the expected type.
      const data = ev.data as ChannelsOnboardingMessage | undefined
      if (!data || typeof data !== 'object' || !('type' in data)) {
        // eslint-disable-next-line no-console
        console.debug('[onboarding] message discarded: missing/invalid type field', { data })
        return
      }
      if (popupRef.current && ev.source !== popupRef.current) {
        // eslint-disable-next-line no-console
        console.debug('[onboarding] message discarded: source is not our popup')
        return
      }
      if (data.type === 'channels-onboarding-complete') {
        outcomeDelivered = true
        cleanup()
        setState('completed')
        void Promise.resolve(onComplete()).catch(() => undefined)
        return
      }
      if (data.type === 'channels-onboarding-error') {
        outcomeDelivered = true
        cleanup()
        setError(`${provider} onboarding failed: ${data.error}`)
        setState('failed')
      }
      if (data.type === 'channels-onboarding-pending-asset-selection') {
        // eslint-disable-next-line no-console
        console.debug('[onboarding] pending-asset-selection received', { provider, assetCount: data.assets.length })
        outcomeDelivered = true
        cleanup()
        setState('pending_asset_selection')
        onPendingAssetSelection?.(data.assets)
      }
    }
    messageHandlerRef.current = handleMessage
    window.addEventListener('message', handleMessage)

    const finishAsComplete = (): void => {
      setState('completed')
      void Promise.resolve(onComplete()).catch(() => undefined)
    }

    // Called once the popup has closed with no postMessage ever delivered.
    // For providers whose callback always requires a selection step (YouTube,
    // and now Meta's facebook/instagram/whatsapp), a blind "treat as
    // complete" is wrong — we must check the server for a pending selection
    // before assuming there's nothing to do, otherwise the channel picker
    // silently never opens and nothing gets connected. Some browsers/
    // extensions (observed in production with React DevTools active)
    // prevent postMessage from ever reaching this window even though
    // window.opener stays set.
    const pendingSelectionEndpoint =
      provider === 'youtube' ? '/admin/channels/youtube/pending-selection'
      : provider === 'facebook' || provider === 'instagram' || provider === 'whatsapp' ? '/admin/channels/meta/pending-selection'
      : null

    const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

    const handlePopupClosedWithNoMessage = async (): Promise<void> => {
      if (!pendingSelectionEndpoint) {
        // eslint-disable-next-line no-console
        console.debug('[onboarding] popup closed without postMessage, treating as complete')
        finishAsComplete()
        return
      }
      // eslint-disable-next-line no-console
      console.debug('[onboarding] popup closed without postMessage; checking for a pending selection', { provider })
      for (let attempt = 1; attempt <= PENDING_SELECTION_MAX_ATTEMPTS; attempt++) {
        try {
          const res = await api.get<{ pending: boolean; assets?: Array<{ id: string; name: string }> }>(
            pendingSelectionEndpoint,
          )
          if (res.pending && res.assets) {
            // eslint-disable-next-line no-console
            console.debug('[onboarding] pending selection found via fallback poll', { provider, attempt, assetCount: res.assets.length })
            setState('pending_asset_selection')
            onPendingAssetSelection?.(res.assets)
            return
          }
        } catch {
          // eslint-disable-next-line no-console
          console.debug('[onboarding] pending-selection check errored, will retry', { provider, attempt })
        }
        if (attempt < PENDING_SELECTION_MAX_ATTEMPTS) {
          await sleep(PENDING_SELECTION_RETRY_MS)
        }
      }
      // eslint-disable-next-line no-console
      console.debug('[onboarding] no pending selection found after retrying; treating as complete', { provider })
      finishAsComplete()
    }

    // Fallback completion signal: only fires if NO postMessage ever arrived
    // (outcomeDelivered false); if one already did, the popup closing
    // afterward is expected and must not re-trigger a different outcome.
    pollIntervalRef.current = setInterval(() => {
      if (outcomeDelivered) return
      if (popupRef.current?.closed) {
        outcomeDelivered = true
        cleanup()
        void handlePopupClosedWithNoMessage()
      }
    }, POPUP_POLL_MS)

    timeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.debug('[onboarding] timed out waiting for popup completion')
      cleanup()
      setState('timeout')
      void Promise.resolve(onComplete()).catch(() => undefined)
    }, TIMEOUT_MS)

    setState('awaiting')
  }, [provider, onComplete, onPendingAssetSelection, cleanup])

  return { state, error, launch, cancel }
}
